/**
 * Boards over MCP — the READ half. Writes live in `boardWrites.js`.
 *
 * Phase 1 was read-only, and not because writes were unfinished: the app's own
 * whole-board writer goes through `extension_records_sync_scope`, which
 * TOMBSTONES every record in the scope that is absent from the array it is
 * given — so a read-modify-write from a stale external client would delete
 * tasks a human just added. Phase 2's answer is not "be careful with that
 * path", it is that MCP never uses it: every board write is one record through
 * `extension_records_cas_upsert`, which is what the per-task LWW model was
 * built for. A test forbids this directory from even naming the whole-scope
 * API.
 *
 * Comment EDIT and DELETE stay out for a related reason: `mergeComments`
 * expresses a delete as *absence plus a newer `updatedAt`*, an encoding an
 * external client cannot be trusted with, where a mistake silently resurrects
 * or destroys a teammate's comment. Appending one is the most that is on the
 * table (`board_comment_add`).
 *
 * **A board write must refresh the UI, and the ORDER is not optional.** A
 * record-granular write into a board the user has OPEN is invisible to that
 * tab's in-memory snapshot, and the tab persists the whole board on a debounce
 * — so its next persist tombstones the record MCP just wrote. Bumping the
 * boards nonce on its own makes that WORSE, because the nonce path flushes the
 * stale snapshot before it re-reads. `mcp/uiRefresh.js` therefore publishes the
 * record into the open tab first and bumps second; see `mcp/boardChanges.js`
 * for the whole chain.
 *
 * `board_read` carries a `rev` on the board head and on every list and task,
 * from `extension_records_list_meta`. That is a hard dependency of the write
 * half, not a nicety: without it an agent physically cannot satisfy the
 * `expected_rev` that `board_update`, `board_list_rename` and
 * `board_task_update` require, and every one of them would be unusable.
 *
 * `board_read` is BUDGETED, not silently capped. The AI path's
 * `BOARD_TASK_LIMIT 60` / `BOARD_DESCRIPTION_LIMIT 400` exist to protect a
 * model's context window; an external agent legitimately wants the whole
 * board, so this returns everything up to a byte budget and says `truncated`
 * with a cursor when it cannot.
 */
import { invoke } from '@tauri-apps/api/core';
import {
  listBoardSummaries,
  readBoard,
  teamScopeId,
  boardScopeKey,
} from '../../extensions/bundled/task-manager/boardsStore';
import { isOverSyncHint } from '../boardRecords';
import { toStoredTask } from '../../extensions/bundled/task-manager/taskDescriptionCodec';
import { taskText, boardFormatOf } from '../boardTaskText';
import { taskStatusOf } from '../../extensions/bundled/task-manager/taskStatus';
import { subtasksOf } from '../../extensions/bundled/task-manager/subtasks';
import { boardPrefixOf, taskKeysFor } from '../../extensions/bundled/task-manager/taskNumbers';
import { prefixAliasesOf } from '../../extensions/bundled/task-manager/taskKeyPrefixEdit';
import { publicCommitLinks } from '../../extensions/bundled/task-manager/taskCommitLinks';
import { personalScopeId } from '../../services/personalScope';
import { boardUri, resourceEntry, resourceLink } from '../resources';
import { resolveScopes, PERSONAL } from '../scopes';
import { mcpError } from '../errors';

/**
 * Wire scope → the engine scope key boardsStore takes. Exported since phase 2:
 * `boardWrites.js` must resolve a scope the identical way or a write could land
 * in a workspace the read half never looks at.
 */
export function scopeKeyFor(wire) {
  if (!wire || wire === PERSONAL) return personalScopeId();
  return teamScopeId(wire.slice('team:'.length));
}

/** Engine scope key → wire scope, so a summary can name where it came from. */
function wireFor(scopeKey) {
  if (typeof scopeKey === 'string' && scopeKey.startsWith('team:')) return scopeKey;
  return PERSONAL;
}

export async function boardsList({ scope }) {
  const { consulted, unavailable } = resolveScopes(scope);
  let summaries = [];
  const missed = [...unavailable];
  try {
    // `listBoardSummaries` already walks every scope the user can see and
    // swallows a per-scope failure, so the scope accounting above is the only
    // signal a caller gets about completeness — which is exactly why every
    // result carries it.
    summaries = await listBoardSummaries();
  } catch {
    // Nothing was read, so nothing was consulted and EVERYTHING is unavailable
    // — including the scopes `resolveScopes` had already given up on. Reporting
    // only `consulted` here inverted the answer in the case that matters most:
    // for an unknown requested scope `consulted` is empty, so a total failure
    // came back as zero boards and an EMPTY `scopesUnavailable`, i.e. "the user
    // has no boards" stated as fact.
    return {
      boards: [],
      resources: [],
      resourceLinks: [],
      scopesConsulted: [],
      scopesUnavailable: [...new Set([...consulted, ...missed])],
    };
  }
  const filtered = scope
    ? summaries.filter((b) => wireFor(b.scopeKey) === scope)
    : summaries;
  return {
    boards: filtered.map((b) => ({
      id: b.id,
      name: b.name,
      description: b.description || '',
      taskCount: b.taskCount,
      updatedAt: b.updatedAt ?? null,
      visibility: b.visibility,
      scope: wireFor(b.scopeKey),
      uri: boardUri(wireFor(b.scopeKey), b.id),
    })),
    resources: filtered.map((b) =>
      resourceEntry({
        uri: boardUri(wireFor(b.scopeKey), b.id),
        name: b.name || b.id,
        title: b.name || undefined,
        description: b.description || undefined,
        mimeType: 'application/json',
      })
    ),
    resourceLinks: filtered.map((b) =>
      resourceLink(boardUri(wireFor(b.scopeKey), b.id), b.name || b.id)
    ),
    scopesConsulted: consulted,
    scopesUnavailable: missed,
  };
}

/** Budget for one `board_read`, in bytes of serialised JSON. */
export const BOARD_MAX_BYTES = 256 * 1024;

/**
 * `id -> rev` for every record of one board.
 *
 * Best-effort: a metadata read that fails leaves every `rev` null rather than
 * failing the whole read. That degradation is VISIBLE, not silent — a null rev
 * means the update tools refuse with `expected_rev_required` and the agent is
 * told why, rather than a write going through against an unknown base.
 */
async function revsForBoard(board) {
  try {
    const rows = await invoke('extension_records_list_meta', {
      workspaceId: boardScopeKey(board),
      extensionId: 'task-manager',
      recordType: null,
      scopeId: String(board.id),
    });
    return new Map((rows || []).map((r) => [r.id, r.rev ?? null]));
  } catch {
    return new Map();
  }
}

export async function boardRead({
  board_id: boardId,
  scope,
  include_comments: includeComments,
  cursor,
  format: rawFormat,
}) {
  const wire = scope || PERSONAL;
  const format = boardFormatOf(rawFormat);
  const board = await readBoard(String(boardId), scope ? scopeKeyFor(wire) : undefined);
  if (!board) return mcpError('not_found', 'No board with that id in this scope.');

  const revs = await revsForBoard(board);
  const start = Number.parseInt(cursor ?? '0', 10);
  const from = Number.isFinite(start) && start >= 0 ? start : 0;
  const keys = taskKeysFor(board);
  const allTasks = (board.tasks || []).map((t) => ({
    // `description` and comment `body` in `format`; never the raw editor HTML
    // or block snapshot, and never an image key (cards MCP 7 + MCP 1).
    ...taskText(includeComments ? t : { ...t, comments: undefined }, format),
    rev: revs.get(t.id) ?? null,
    // The task's key ("WEB-4"): what a commit message or branch names to link
    // it, and what `board_task_link_commit` links a commit to by id.
    key: keys.get(String(t.id)) || null,
    // What the app shows (card ef074711): To do / In progress / Done /
    // Dismissed, reconciled with `done`; and the priority the card displays —
    // an unset priority reads as medium there.
    status: taskStatusOf(t),
    priority: ['low', 'medium', 'high'].includes(t.priority) ? t.priority : 'medium',
    // The checklist, normalised (a task written before subtasks existed reads
    // as []). Ids are what `board_task_update`'s replacement list takes back.
    subtasks: subtasksOf(t),
    // Commit links (commit ↔ task links): without the repository's URL, id or
    // name — perimeter MCP 8, no repository detail leaves over MCP.
    commitLinks: publicCommitLinks(t),
    // Diagnostic only: a task whose record has already crossed the sync cap is
    // stored and readable and will never reach the user's other devices. The
    // app gives a human no signal at all here; an agent at least gets one.
    // Measured as stored (the description snapshot compact, taskDescriptionCodec).
    syncable: !isOverSyncHint(toStoredTask(t)),
  }));

  // `readBoard` falls back to a CROSS-SCOPE search when the id is not in the
  // scope it was asked for, so the requested `wire` is not necessarily where
  // the board lives. Label it with the board's OWN scope (derived from its
  // visibility/teamId, exactly as `boardsList` labels a summary from its
  // `scopeKey`) — otherwise a team board read without a `scope` argument comes
  // back claiming `scope: "personal"` and mints a second `apiboo://` URI for a
  // board `boards_list` already named under its team scope. One board, one URI,
  // and provenance that is true.
  const actualWire = wireFor(boardScopeKey(board));
  const head = {
    id: board.id,
    name: board.name,
    description: board.description || '',
    scope: actualWire,
    requestedScope: wire,
    // What `description` and comment `body` are written in.
    format,
    uri: boardUri(actualWire, board.id),
    visibility: board.visibility ?? 'private',
    updatedAt: board.updatedAt ?? null,
    // Task keys (commit ↔ task links): the prefix every task's `key` starts
    // with, and the board's old prefixes — a key with one still names the task.
    keyPrefix: boardPrefixOf(board),
    keyPrefixAliases: prefixAliasesOf(board),
    // The board head's own rev, for `board_update`.
    rev: revs.get(board.id) ?? null,
    labels: board.labels || [],
    // Show completed tasks / progress counts under each list name.
    showDoneTasks: board.showDoneTasks !== false,
    showColumnProgress: board.showColumnProgress !== false,
    groups: (board.groups || []).map((g) => ({ ...g, rev: revs.get(g.id) ?? null })),
  };
  const budget = BOARD_MAX_BYTES - JSON.stringify(head).length;

  const tasks = [];
  let used = 0;
  let index = from;
  while (index < allTasks.length) {
    const size = JSON.stringify(allTasks[index]).length + 1;
    if (used + size > budget && tasks.length > 0) break;
    tasks.push(allTasks[index]);
    used += size;
    index += 1;
  }
  const truncated = index < allTasks.length;
  return {
    ...head,
    tasks,
    taskCount: allTasks.length,
    truncated,
    ...(truncated ? { nextCursor: String(index) } : {}),
  };
}
